import { Fragment, useRef } from 'react'
import { AssetReference } from '@opallabs/cassini'
import { ModularComponentBuilder } from '@opallabs/periodic/src/ModularComponentBuilder'
import {
  GetContentByIDV2Return,
  LinkWithData,
  OnAddSectionProps,
  OnLinkDataUpdate,
  OnShowAssetPickerProps,
  Module,
  ModuleDefinition,
} from '../../../helpers/types'
import { TwitterMomentHeader } from '../TwitterMomentHeader'
import getMomentModule from './getMomentModule'

export type MomentStreamProps = {
  accountImage: AssetReference | null
  accountName: string
  accountUsername: string
  asset: AssetReference | null
  availableModules: ModuleDefinition[]
  description: string
  display: string
  getContentByIdV2?: (id: number) => Promise<GetContentByIDV2Return>
  isPresenting: boolean
  linksWithData: LinkWithData[]
  modules: Module[]
  onAddSection: (args: OnAddSectionProps) => void
  onDeleteSection: (indexToRemove: number) => void
  onDescriptionChange: (val: string) => void
  onDragDrop: (e: React.DragEvent<HTMLDivElement>) => void
  onExpandAsset: (assetId: number) => void
  onRemoveAsset: (assetId: number) => void
  onShowAssetPicker: (args: OnShowAssetPickerProps) => void
  onTitleChange: (val: string) => void
  postType: string
  readOnly: boolean
  scheduledAt: string
  timezone: string
  title: string
  updateCoverAsset: (
    assetData: FileList,
    assetId: number,
    index: number
  ) => void
  updateLinkData: OnLinkDataUpdate
  updateSectionPostOption: (index: number, value: string, name: string) => void
  uploadAssets: (assets: FileList, args: OnShowAssetPickerProps) => void
}

export const Stream: React.FC<MomentStreamProps> = ({
  accountImage,
  accountName,
  accountUsername,
  asset,
  availableModules,
  description,
  display = 'mobileStream',
  getContentByIdV2,
  isPresenting,
  linksWithData,
  modules,
  onAddSection,
  onDeleteSection,
  onDescriptionChange,
  onDragDrop,
  onExpandAsset,
  onRemoveAsset,
  onShowAssetPicker,
  onTitleChange,
  postType,
  readOnly,
  scheduledAt,
  timezone,
  title,
  updateCoverAsset,
  updateLinkData,
  updateSectionPostOption,
  uploadAssets,
}) => {
  const headerRef = useRef<HTMLDivElement>(null)

  return (
    <Fragment>
      <div ref={headerRef}>
        <TwitterMomentHeader
          asset={asset}
          accountImage={accountImage}
          accountName={accountName}
          accountUsername={accountUsername}
          isPresenting={isPresenting}
          scheduledAt={scheduledAt}
          title={title}
          description={description}
          display={display}
          postType={postType}
          onDescriptionChange={onDescriptionChange}
          onTitleChange={onTitleChange}
          onDragDrop={onDragDrop}
          onExpandAsset={onExpandAsset}
          onRemoveAsset={onRemoveAsset}
          onShowAssetPicker={onShowAssetPicker}
          readOnly={readOnly}
          timezone={timezone}
        />
      </div>
      <ModularComponentBuilder
        allProps={{
          accountImage,
          accountName,
          accountUsername,
          display,
          isPresenting,
          readOnly,
          linksWithData,
          timezone,
          postType,
        }}
        displacementHeight={
          headerRef && headerRef.current ? headerRef.current.offsetHeight : 0
        }
        getContentByIdV2={getContentByIdV2}
        getModule={getMomentModule}
        addComponentText="Add Post"
        availableModules={availableModules}
        modules={modules}
        onAddSection={onAddSection}
        onDeleteSection={onDeleteSection}
        onRemoveAsset={onRemoveAsset}
        onExpandAsset={onExpandAsset}
        onShowAssetPicker={onShowAssetPicker}
        updateCoverAsset={updateCoverAsset}
        updateLinkData={updateLinkData}
        updateSectionPostOption={updateSectionPostOption}
        uploadAssets={uploadAssets}
        width={display === 'mobileStream' ? 375 : 600}
      />
    </Fragment>
  )
}
